import {
  validateArenaDef,
  validateBoardDef,
  validateRoster,
  validateTilesetDef,
  type ArenaDef,
  type BoardDef,
  type Roster,
  type TilesetDef,
} from '@archon3d/core';
import forestGlade from '../arenas/forest-glade.json';
import classic9x9 from '../boards/classic-9x9.json';
import hexCrown from '../boards/hex-crown.json';
import classicRoster from '../characters/classic.json';
import fantasyTileset from '../tilesets/fantasy.json';

export const DEFAULT_BOARD_ID = 'classic-9x9';
export const DEFAULT_ROSTER_ID = 'classic';
export const DEFAULT_ARENA_ID = 'forest-glade';
export const DEFAULT_TILESET_ID = 'fantasy';

/** Bundled boards, validated once at load time. */
export const boards: Readonly<Record<string, BoardDef>> = Object.freeze(
  Object.fromEntries([classic9x9, hexCrown].map((raw) => [raw.id, validateBoardDef(raw)])),
);

/** Bundled rosters, validated once at load time. */
export const rosters: Readonly<Record<string, Roster>> = Object.freeze({
  [DEFAULT_ROSTER_ID]: validateRoster(classicRoster),
});

/** Bundled tilesets, validated once at load time; arenas name one of these. */
export const tilesets: Readonly<Record<string, TilesetDef>> = Object.freeze(
  Object.fromEntries([fantasyTileset].map((raw) => [raw.id, validateTilesetDef(raw)])),
);

/** Bundled battle arenas, validated once at load time against their tilesets. */
export const arenas: Readonly<Record<string, ArenaDef>> = Object.freeze(
  Object.fromEntries([forestGlade].map((raw) => [raw.id, validateArenaDef(raw, tilesets)])),
);

/** The tileset an arena draws its tile families from. */
export function tilesetFor(arena: ArenaDef): TilesetDef {
  const tileset = tilesets[arena.tileset];
  if (!tileset) throw new Error(`Arena '${arena.id}' names unknown tileset '${arena.tileset}'`);
  return tileset;
}
import originalGame from '../games/original.json';
import { fingerprint, validateGameDefinition, type GameDefinition } from '@archon3d/core';
/** Independent, versioned editor seed; never resolves live classic files. */
export const ORIGINAL_GAME: GameDefinition = validateGameDefinition(originalGame);
import originalHex from '../games/original-hex.json';
export const ORIGINAL_HEX_GAME: GameDefinition = validateGameDefinition(originalHex);
export const ORIGINAL_GAMES = [ORIGINAL_GAME, ORIGINAL_HEX_GAME] as const;

/** Taken as the package loads (88 KB, a few milliseconds), before anything could change it. */
const DIGEST = fingerprint({ boards, rosters, tilesets, arenas, games: ORIGINAL_GAMES });
/**
 * A fingerprint of everything this package ships: boards, rosters, tilesets, arenas and the
 * original games. A client and a server built from different content replay the same history
 * into different games, so the server names its digest in `hello`, and a client whose own
 * differs asks for an update rather than play on out of step (core-net-balance:bugs#7).
 */
export function contentDigest(): string {
  return DIGEST;
}
